import {daysDiffInclusive, parseDDMMYYYY} from '@/utils/dataUtils';

/**
 * Inputs for PPE Depn chg Final formula (aligned with PropertySubTable.computeRow).
 * Bases should already include existing field adjustments when applicable.
 */
export type PpeDepnFormulaInput = {
    costBf: number;
    additions: number;
    accDepBf: number;
    /** True when raw additions is null/undefined and there is no additions field adj yet */
    additionsEmpty: boolean;
    date: string | null | undefined;
    /** Normalized Dep rate (e.g. 0.04 for 4%) */
    depRate: number;
    periodFrom: string | null | undefined;
    periodTo: string | null | undefined;
};

export type PpeCostFieldAdjKey = 'costAdj' | 'addAdj';

export type PpeDepnDeltaContext = {
    costBf: number;
    additions: number | null | undefined;
    accDepBf: number;
    date: string | null | undefined;
    depRate: number;
    periodFrom: string | null | undefined;
    periodTo: string | null | undefined;
    /** User override of Depn chg Final — freezes formula delta */
    depnChgChange?: number | null;
    existingCostAdj?: number;
    existingAddAdj?: number;
    /** Legacy Acc dep b/f field adj sum — kept so Cost/Additions ΔDepn companion stays accurate */
    existingAccDepAdj?: number;
};

/**
 * Formula-only Depn chg Final (no override, no depnChgAdjs).
 * Matches PropertySubTable.computeRow calculatedDepnChgFinal.
 */
export function computePpeCalculatedDepnChgFinal(input: PpeDepnFormulaInput): number {
    const costBf = Number(input.costBf) || 0;
    const additions = Number(input.additions) || 0;
    const accDepBf = Number(input.accDepBf) || 0;
    const depRate = Number(input.depRate) || 0;

    const periodToDate = parseDDMMYYYY(input.periodTo ?? null);
    const periodFromDate = parseDDMMYYYY(input.periodFrom ?? null);
    const date = parseDDMMYYYY(input.date ?? null);

    if (!periodToDate || !periodFromDate || !date || !depRate) {
        return 0;
    }

    const dayCount = daysDiffInclusive(periodToDate, date);
    const yearDays = daysDiffInclusive(periodToDate, periodFromDate);
    if (yearDays <= 0) return 0;

    let depnChgOriginal = 0;
    if (input.additionsEmpty) {
        if (dayCount >= 365) {
            depnChgOriginal = Math.round(costBf * depRate);
        } else {
            depnChgOriginal = Math.round((costBf * depRate / yearDays) * dayCount);
        }
    } else {
        if (costBf > 0) {
            depnChgOriginal += Math.round(costBf * depRate);
        }
        depnChgOriginal += Math.round((additions * depRate / yearDays) * dayCount);
    }

    const totalCost = costBf + additions;
    const baseAuto = totalCost - accDepBf - depnChgOriginal;
    const depnChgAutoDeduct = baseAuto < 0 ? baseAuto : 0;
    return depnChgOriginal + depnChgAutoDeduct;
}

/**
 * ΔDepn when applying a Cost b/f or Additions field adjustment.
 * Returns 0 when override is set or formula inputs are incomplete.
 */
export function computePpeDepnDeltaForCostFieldAdj(
    ctx: PpeDepnDeltaContext,
    fieldKey: PpeCostFieldAdjKey,
    proposedAdj: number,
): number {
    const proposed = Number(proposedAdj) || 0;
    if (Math.abs(proposed) < 0.001) return 0;
    if (ctx.depnChgChange != null) return 0;

    const existingCostAdj = Number(ctx.existingCostAdj) || 0;
    const existingAddAdj = Number(ctx.existingAddAdj) || 0;
    const existingAccDepAdj = Number(ctx.existingAccDepAdj) || 0;

    const costBf0 = (Number(ctx.costBf) || 0) + existingCostAdj;
    const add0 = (Number(ctx.additions) || 0) + existingAddAdj;
    const acc0 = (Number(ctx.accDepBf) || 0) + existingAccDepAdj;
    const additionsEmpty0 = ctx.additions == null && existingAddAdj === 0;

    const costBf1 = costBf0 + (fieldKey === 'costAdj' ? proposed : 0);
    const add1 = add0 + (fieldKey === 'addAdj' ? proposed : 0);
    const additionsEmpty1 = fieldKey === 'addAdj'
        ? false
        : (ctx.additions == null && existingAddAdj === 0);

    const shared = {
        accDepBf: acc0,
        date: ctx.date,
        depRate: ctx.depRate,
        periodFrom: ctx.periodFrom,
        periodTo: ctx.periodTo,
    };

    const before = computePpeCalculatedDepnChgFinal({
        ...shared,
        costBf: costBf0,
        additions: add0,
        additionsEmpty: additionsEmpty0,
    });
    const after = computePpeCalculatedDepnChgFinal({
        ...shared,
        costBf: costBf1,
        additions: add1,
        additionsEmpty: additionsEmpty1,
    });
    return after - before;
}

export function isPpeCostFieldAdjKey(key: string | null | undefined): key is PpeCostFieldAdjKey {
    return key === 'costAdj' || key === 'addAdj';
}
